package es.cragma.fichajefacil.ui.screens.auth

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.model.CompanyPlan
import es.cragma.fichajefacil.model.CountryData
import es.cragma.fichajefacil.ui.components.CragmaHeader
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.components.StyledButtonWhite
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CompanyRegisterScreen(
    currentLanguage: AppLanguage = AppLanguage.SPANISH,
    onRegisterSuccess: (
        companyName: String,
        cif: String,
        address: String,
        city: String,
        country: String,
        plan: CompanyPlan,
        adminName: String,
        adminPhone: String,
        adminEmail: String,
        adminPassword: String
    ) -> Unit,
    onNavigateBackToLogin: () -> Unit
) {
    var currentStep by remember { mutableStateOf(1) } // 1, 2, or 3

    var companyName by remember { mutableStateOf("") }
    var cif by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var selectedCountry by remember { mutableStateOf("España") }
    var isCountryDropdownExpanded by remember { mutableStateOf(false) }
    var countrySearchQuery by remember { mutableStateOf("") }

    var adminName by remember { mutableStateOf("") }
    var adminPhone by remember { mutableStateOf("") }
    var adminEmail by remember { mutableStateOf("") }
    var adminPassword by remember { mutableStateOf("") }

    var selectedPlan by remember { mutableStateOf(CompanyPlan.FREE) }

    var errorMessage by remember { mutableStateOf<String?>(null) }
    val scrollState = rememberScrollState()

    val filteredCountries = remember(countrySearchQuery) {
        if (countrySearchQuery.isBlank()) {
            CountryData.worldCountries
        } else {
            CountryData.worldCountries.filter { it.contains(countrySearchQuery, ignoreCase = true) }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .windowInsetsPadding(WindowInsets.safeDrawing)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Spacer(modifier = Modifier.height(16.dp))

            CragmaHeader(
                title = "Registro de Empresa",
                subtitle = "Crea tu espacio de trabajo corporativo en 3 sencillos pasos"
            )

            Spacer(modifier = Modifier.height(20.dp))

            // Step Progress Indicator Bar
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 560.dp),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = when (currentStep) {
                                1 -> "Paso 1 de 3: Datos de la Empresa"
                                2 -> "Paso 2 de 3: Cuenta Administrador"
                                else -> "Paso 3 de 3: Plan de Suscripción"
                            },
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                        Text(
                            text = "$currentStep / 3",
                            fontSize = 13.sp,
                            fontWeight = FontWeight.ExtraBold,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    LinearProgressIndicator(
                        progress = { currentStep / 3f },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(6.dp),
                        color = MaterialTheme.colorScheme.primary,
                        trackColor = MaterialTheme.colorScheme.surfaceVariant
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Step Content Card
            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 560.dp),
                shape = RoundedCornerShape(20.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.dp)
                ) {
                    if (errorMessage != null) {
                        Surface(
                            color = Color(0xFFFEE2E2),
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(bottom = 16.dp)
                        ) {
                            Text(
                                text = errorMessage!!,
                                color = CragmaRedDanger,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(10.dp),
                                textAlign = TextAlign.Center
                            )
                        }
                    }

                    when (currentStep) {
                        // --- STEP 1: DATOS BÁSICOS DE LA EMPRESA ---
                        1 -> {
                            Text(
                                text = "1. Datos Básicos de la Empresa",
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Text(
                                text = "Introduce los datos identificativos de tu organización.",
                                fontSize = 12.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(top = 2.dp, bottom = 16.dp)
                            )

                            OutlinedTextField(
                                value = companyName,
                                onValueChange = {
                                    companyName = it
                                    errorMessage = null
                                },
                                label = { Text("Nombre Legal de la Empresa *") },
                                placeholder = { Text("Ej: MiEmpresa Servicios S.L.") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = cif,
                                onValueChange = {
                                    cif = it
                                    errorMessage = null
                                },
                                label = { Text("CIF / NIF de la Empresa *") },
                                placeholder = { Text("Ej: B12345678") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = address,
                                onValueChange = { address = it },
                                label = { Text("Dirección Legal (Opcional)") },
                                placeholder = { Text("Ej: Av. Gran Vía 42, Planta 3") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            // Country Dropdown
                            ExposedDropdownMenuBox(
                                expanded = isCountryDropdownExpanded,
                                onExpandedChange = { isCountryDropdownExpanded = !isCountryDropdownExpanded },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = selectedCountry,
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text("País (Opcional)") },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = isCountryDropdownExpanded) },
                                    modifier = Modifier
                                        .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
                                        .fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )

                                ExposedDropdownMenu(
                                    expanded = isCountryDropdownExpanded,
                                    onDismissRequest = { isCountryDropdownExpanded = false }
                                ) {
                                    OutlinedTextField(
                                        value = countrySearchQuery,
                                        onValueChange = { countrySearchQuery = it },
                                        placeholder = { Text("Buscar país...") },
                                        singleLine = true,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(8.dp)
                                    )

                                    filteredCountries.forEach { countryOption ->
                                        DropdownMenuItem(
                                            text = { Text(countryOption) },
                                            onClick = {
                                                selectedCountry = countryOption
                                                isCountryDropdownExpanded = false
                                                countrySearchQuery = ""
                                            }
                                        )
                                    }
                                }
                            }

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = city,
                                onValueChange = { city = it },
                                label = { Text("Ciudad / Municipio (Opcional)") },
                                placeholder = { Text("Ej: Madrid, Barcelona, Málaga...") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(24.dp))

                            StyledButtonPrimary(
                                text = "Siguiente: Cuenta Admin →",
                                onClick = {
                                    if (companyName.isBlank() || cif.isBlank()) {
                                        errorMessage = "Por favor, introduce el Nombre Legal y el CIF de la empresa."
                                    } else {
                                        errorMessage = null
                                        currentStep = 2
                                    }
                                }
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            StyledButtonWhite(
                                text = "← Volver al Inicio de Sesión",
                                onClick = onNavigateBackToLogin
                            )
                        }

                        // --- STEP 2: CUENTA ADMINISTRADOR ---
                        2 -> {
                            Text(
                                text = "2. Cuenta Administrador de Empresa",
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Text(
                                text = "Esta cuenta administrará totalmente la empresa y añadirá a los empleados.",
                                fontSize = 12.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(top = 2.dp, bottom = 16.dp)
                            )

                            OutlinedTextField(
                                value = adminName,
                                onValueChange = { adminName = it },
                                label = { Text("Nombre y Apellidos del Administrador (Opcional)") },
                                placeholder = { Text("Ej: Juan Pérez") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = adminPhone,
                                onValueChange = { adminPhone = it },
                                label = { Text("Teléfono de Contacto (Opcional)") },
                                placeholder = { Text("Ej: 600 111 222") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = adminEmail,
                                onValueChange = {
                                    adminEmail = it
                                    errorMessage = null
                                },
                                label = { Text("Correo Electrónico Corporativo *") },
                                placeholder = { Text("admin@miempresa.com") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = adminPassword,
                                onValueChange = {
                                    adminPassword = it
                                    errorMessage = null
                                },
                                label = { Text("Contraseña de Acceso *") },
                                singleLine = true,
                                visualTransformation = PasswordVisualTransformation(),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Spacer(modifier = Modifier.height(24.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                StyledButtonWhite(
                                    text = "← Anterior",
                                    onClick = {
                                        errorMessage = null
                                        currentStep = 1
                                    },
                                    modifier = Modifier.weight(1f)
                                )

                                StyledButtonPrimary(
                                    text = "Siguiente: Plan →",
                                    onClick = {
                                        if (adminEmail.isBlank() || adminPassword.isBlank()) {
                                            errorMessage = "Por favor, introduce el Correo Electrónico y la Contraseña del Administrador."
                                        } else {
                                            errorMessage = null
                                            currentStep = 3
                                        }
                                    },
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }

                        // --- STEP 3: PLAN DE SUSCRIPCIÓN ---
                        3 -> {
                            Text(
                                text = "3. Elige tu Plan de Suscripción",
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Text(
                                text = "Selecciona la capacidad que mejor se adapte a tu plantilla.",
                                fontSize = 12.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(top = 2.dp, bottom = 16.dp)
                            )

                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(10.dp)
                            ) {
                                CompanyPlan.entries.forEach { plan ->
                                    val isSelected = selectedPlan == plan

                                    val (badgeText, badgeColor, badgeTextColor) = when (plan) {
                                        CompanyPlan.FREE -> Triple("🎁 0€ / Gratis", Color(0xFFDCFCE7), Color(0xFF15803D))
                                        CompanyPlan.BRONZE -> Triple("🔥 Popular", Color(0xFFFEF3C7), Color(0xFFB45309))
                                        CompanyPlan.SILVER -> Triple("⭐ Recomendado", Color(0xFFDBEAFE), Color(0xFF1E40AF))
                                        CompanyPlan.GOLD -> Triple("🚀 Completo", Color(0xFFF3E8FF), Color(0xFF6B21A8))
                                    }

                                    val (priceText, limitText) = when (plan) {
                                        CompanyPlan.FREE -> Pair("0€ / Gratis", "Hasta 5 usuarios")
                                        CompanyPlan.BRONZE -> Pair("3€ / mes (o 20€ / año)", "Hasta 10 usuarios")
                                        CompanyPlan.SILVER -> Pair("5€ / mes (o 45€ / año)", "Hasta 30 usuarios")
                                        CompanyPlan.GOLD -> Pair("10€ / mes (o 100€ / año)", "Hasta 50 usuarios")
                                    }

                                    Card(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable { selectedPlan = plan },
                                        shape = RoundedCornerShape(14.dp),
                                        border = if (isSelected) BorderStroke(2.dp, MaterialTheme.colorScheme.primary) else BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                        colors = CardDefaults.cardColors(
                                            containerColor = if (isSelected) MaterialTheme.colorScheme.surfaceVariant else MaterialTheme.colorScheme.surface
                                        ),
                                        elevation = CardDefaults.cardElevation(defaultElevation = if (isSelected) 4.dp else 1.dp)
                                    ) {
                                        Column(modifier = Modifier.padding(14.dp)) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth(),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    Text(
                                                        text = "Plan ${plan.displayName}",
                                                        fontSize = 15.sp,
                                                        fontWeight = FontWeight.Bold,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                    Spacer(modifier = Modifier.width(8.dp))
                                                    Surface(
                                                        color = badgeColor,
                                                        shape = RoundedCornerShape(8.dp)
                                                    ) {
                                                        Text(
                                                            text = badgeText,
                                                            color = badgeTextColor,
                                                            fontSize = 10.sp,
                                                            fontWeight = FontWeight.Bold,
                                                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                                                        )
                                                    }
                                                }
                                            }

                                            Spacer(modifier = Modifier.height(6.dp))

                                            Text(
                                                text = "👥 $limitText • 💰 $priceText",
                                                fontSize = 12.sp,
                                                fontWeight = FontWeight.SemiBold,
                                                color = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    }
                                }

                                // Contact Card for >50 users
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    colors = CardDefaults.cardColors(containerColor = Color(0xFFEFF6FF)),
                                    shape = RoundedCornerShape(14.dp)
                                ) {
                                    Row(
                                        modifier = Modifier.padding(12.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Text("💬", fontSize = 20.sp, modifier = Modifier.padding(end = 10.dp))
                                        Column {
                                            Text(
                                                text = "Para más de 50 usuarios, contáctanos",
                                                fontSize = 12.sp,
                                                fontWeight = FontWeight.Bold,
                                                color = Color(0xFF1E40AF)
                                            )
                                            Text(
                                                text = "Te preparamos un plan a medida adaptado a la plantilla de tu organización.",
                                                fontSize = 11.sp,
                                                color = Color(0xFF1E3A8A)
                                            )
                                        }
                                    }
                                }
                            }

                            Spacer(modifier = Modifier.height(24.dp))

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                StyledButtonWhite(
                                    text = "← Anterior",
                                    onClick = {
                                        errorMessage = null
                                        currentStep = 2
                                    },
                                    modifier = Modifier.weight(1f)
                                )

                                StyledButtonPrimary(
                                    text = "🚀 Crear Empresa",
                                    onClick = {
                                        val finalAdminName = adminName.ifBlank { adminEmail.substringBefore("@").replace(".", " ") }
                                        onRegisterSuccess(
                                            companyName,
                                            cif,
                                            address,
                                            city,
                                            selectedCountry,
                                            selectedPlan,
                                            finalAdminName,
                                            adminPhone,
                                            adminEmail,
                                            adminPassword
                                        )
                                    },
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(20.dp))
        }
    }
}
